import { useCallback, useEffect, useRef } from 'react';

/**
 * Normalize rates line list to a plain array (legacy Immutable lists still supported).
 */
export const toModelListArray = (modelList) => modelList ?? [];

/**
 * Remove one row from a rates line list without mutating the source list.
 */
export const removeModelListRow = (modelList, rowIndex) => {
    if (!modelList || rowIndex < 0 || rowIndex >= modelList.length) return modelList;
    return modelList.filter((_, index) => index !== rowIndex);
};

/**
 * Resolve a row index from AG Grid callbacks. Prefer stable row id over rowIndex,
 * which can be stale after rowData updates (forceRowUpdating / add row).
 */
export const resolveModelListRowIndex = (modelList, rowIndex, rowId) => {
    if (!modelList || modelList.length === 0) return -1;

    if (rowId !== null && rowId !== undefined && rowId !== '') {
        const indexById = modelList.findIndex((item) => String(item.id) === String(rowId));
        if (indexById >= 0) return indexById;
    }

    if (typeof rowIndex === 'number' && rowIndex >= 0 && rowIndex < modelList.length) {
        return rowIndex;
    }

    return -1;
};

/**
 * Immutably update one cell in a rates line list without mutating the row still in the list.
 */
export const updateModelListCell = (modelList, { rowIndex, id, field, value }) => {
    const index = resolveModelListRowIndex(modelList, rowIndex, id);
    if (index < 0) return null;

    const model = modelList[index];
    if (!model) return null;

    const updatedModel = model.immutable;
    updatedModel[field] = value;
    const nextList = [...modelList];
    nextList[index] = updatedModel;
    return nextList;
};

/**
 * Keep the latest rates list in a ref for add/edit handlers that may run
 * before React re-renders (rapid adds, overlapping cell commits).
 */
export const useRatesModelListRef = (modelList) => {
    const modelListRef = useRef(modelList);

    useEffect(() => {
        modelListRef.current = modelList;
    }, [modelList]);

    return modelListRef;
};

export const useAppendToRatesModelList = ({ field, modelListRef, onChange }) =>
    useCallback(
        (newLine) => {
            if (!onChange) return;
            const currentList = modelListRef.current ?? [];
            onChange(field, [...currentList, newLine]);
        },
        [field, modelListRef, onChange],
    );

export const useRatesTableOnChangeCell = ({ field, modelListRef, onChange }) => {
    const onChangeCell = useCallback(
        ({ rowIndex, id, field: fieldKey, value }) => {
            const list = modelListRef.current;
            if (!list || !onChange) return;

            const newModelList = updateModelListCell(list, {
                rowIndex,
                id,
                field: fieldKey,
                value,
            });
            if (newModelList) {
                onChange(field, newModelList);
            }
        },
        [field, modelListRef, onChange],
    );

    const onChangeCellRef = useRef(onChangeCell);
    onChangeCellRef.current = onChangeCell;

    const stableOnChangeCell = useCallback((payload) => {
        onChangeCellRef.current?.(payload);
    }, []);

    return { onChangeCell, stableOnChangeCell };
};
